import { createBrowserSdk, setSdk } from "@chotca/api-client";
import { exchangeRefreshToken } from "@chotca/auth";
import { API_BASE_URL } from "@/env";
import { getAccessToken, setAccessToken } from "./access-token";

const APP_HEADERS = { "X-Chotca-App": "employer" } as const;

let bootstrapped = false;
let refreshing: Promise<boolean> | null = null;

const PUBLIC_AUTH_PATHS = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/claim",
];

function isOnAuthPage(): boolean {
  if (typeof window === "undefined") return false;
  const p = window.location.pathname;
  return PUBLIC_AUTH_PATHS.some(
    (path) => p === path || p.startsWith(`${path}/`),
  );
}

function redirectToLogin(): void {
  if (typeof window === "undefined") return;
  // Already on an auth page — don't loop.
  if (isOnAuthPage()) return;
  const back = window.location.pathname + window.location.search;
  const url = `/login?redirect=${encodeURIComponent(back)}`;
  window.location.assign(url);
}

/**
 * SPA refresh interceptor. Called by the SDK on a 401:
 * 1. POST /auth/refresh with `credentials: include` so the browser attaches
 *    the `.chotca.com` httpOnly refresh cookie. We pass an empty body — the
 *    backend reads the cookie. (The shared `exchangeRefreshToken` accepts a
 *    refresh token string for the Next/middleware path; for SPA we pass an
 *    empty marker and rely on the cookie.)
 * 2. On success, store the new access token in memory and return `true` so
 *    the SDK retries the original request once.
 * 3. On invalid_session, hard-navigate to the apex login page.
 */
async function performRefresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const result = await exchangeRefreshToken("__cookie__", API_BASE_URL, {
      credentials: "include",
      headers: APP_HEADERS,
    });
    if (result.ok) {
      setAccessToken(result.tokens.accessToken);
      return true;
    }
    if (result.reason === "invalid_session") {
      setAccessToken(null);
      redirectToLogin();
    }
    return false;
  })();
  try {
    return await refreshing;
  } finally {
    refreshing = null;
  }
}

export function installSdk(): void {
  if (bootstrapped) return;
  bootstrapped = true;
  setSdk(
    createBrowserSdk({
      baseUrl: API_BASE_URL,
      getAccessToken: async () => getAccessToken(),
      onUnauthorized: async () => performRefresh(),
      extraHeaders: APP_HEADERS,
    }),
  );
}

/**
 * Boot-time auth bootstrap. Called once before mounting React. Tries an
 * eager refresh — succeeds → SPA boots authenticated; fails → first
 * authed query 401s and the interceptor redirects to login.
 */
export async function bootstrapAuth(): Promise<void> {
  await performRefresh();
}
